<script lang="ts">
  import Image from "@lernetz/svelte-image/components/Image.svelte";
  import { requests, setupSharedProps } from "../../setup";
  import { t } from "@lernetz/svelte-translate";
  import { createEventDispatcher } from "svelte";
  import { Icon } from "@lernetz/svelte-icon";
  import { router } from "@inertiajs/svelte";
  import { repeatOnHold } from "../repeatOnHold";

  export let question;
  export let questionNr;
  export let mode = "single";
  export let last = false;
  export let dashboard;
  export let type;

  const params = new URLSearchParams(window.location.search);
  const pupils = Number(params.get("pupils") ?? 20);
  const preview = params.get("preview") ?? "false";
  let pupilsA = Math.ceil(pupils / 2);
  let pupilsB = Math.floor(pupils / 2);
  let showSolution: boolean = false;
  let selectedAnswer = 0;
  let lockAnswers = false;

  const dispatch = createEventDispatcher();

  setupSharedProps();

  function nextQuestion() {
    dispatch("next");
  }

  function selectAnswer(answer: number) {
    if (mode !== "single") return;
    if (lockAnswers) return;

    selectedAnswer = answer;
    lockAnswers = true;
    saveData();
  }

  function minusA() {
    pupilsA = pupilsA - 1;
    pupilsB = pupilsB + 1;
  }
  function plusA() {
    pupilsA = pupilsA + 1;
    pupilsB = pupilsB - 1;
  }

  function saveData() {
    showSolution = true;
    if (preview == "false") {
      let data = {
        dashboardId: dashboard.id,
        questionId: question.id,
        type: type,
        mode: mode,
        right: 0,
        wrong: 0,
      };
      if (mode == "class") {
        if (question.image1correct) {
          data.right = pupilsA;
          data.wrong = pupilsB;
        } else {
          data.right = pupilsB;
          data.wrong = pupilsA;
        }
      } else {
        const correct =
          selectedAnswer === 1
            ? question.image1correct
            : question.image2correct;
        if (correct) {
          data.right = 1;
        } else {
          data.wrong = 1;
        }
      }
      dispatch("updateResults", { right: data.right, wrong: data.wrong });
      router.post("/saveData", data);
    }
  }

  $: imageClasses = [0, 1].map((i) => {
    const correct = i === 0 ? question.image1correct : question.image2correct;

    // CLASS MODE
    if (mode === "class") {
      if (!showSolution) {
        return {
          border: "border-yellow-main",
          circle: "bg-yellow-accent",
          showIcon: false,
        };
      }

      return {
        border: correct ? "border-correct-light" : "border-false-light",
        circle: correct ? "bg-correct-dark" : "bg-false-dark",
        showIcon: true,
      };
    }

    // SINGLE MODE

    if (!showSolution) {
      return {
        border: "border-yellow-main",
        circle: "bg-yellow-accent",
        showIcon: false,
      };
    }

    const selectedCorrect =
      selectedAnswer === 1 ? question.image1correct : question.image2correct;

    // angeklickt & richtig
    if (selectedAnswer === i + 1 && correct) {
      return {
        border: "border-correct-light",
        circle: "bg-correct-dark",
        showIcon: true,
      };
    }

    // angeklickt & falsch
    if (selectedAnswer === i + 1 && !correct) {
      return {
        border: "border-false-light",
        circle: "bg-false-dark",
        showIcon: true,
      };
    }

    // nicht angeklickt & richtige Antwort (nur wenn falsch gewählt)
    if (selectedAnswer !== i + 1 && correct && !selectedCorrect) {
      return {
        border: "border-correct-light",
        circle: "bg-correct-dark",
        showIcon: true,
      };
    }

    // neutral
    return {
      border: "border-yellow-main",
      circle: "bg-yellow-accent",
      showIcon: false,
    };
  });
</script>

<div class="flex flex-col xl:flex-row gap-16 md:gap-24 xl:gap-48 xl:items-start xl:justify-center px-16 md:px-24 w-full max-w-[900px] mx-auto">
  <!-- Question: top on mobile, center on desktop -->
  <div class="order-1 xl:order-2 w-full xl:w-524 xl:mt-48 xl:shrink-0">
    <div class="text-18 font-bold bg-line-subtle rounded-2xl px-10 text-grey-1 w-fit py-4 place-self-center relative z-20">
      {$t("question")} {questionNr + 1} / 10
    </div>
    <div class="font-bold text-28 bg-white py-32 text-blue-main rounded-3xl px-16 -mt-20 relative z-10">
      <div class="text-center">{question.question}</div>
      {#if showSolution}
        <div class="mt-24 bg-yellow-main rounded-2xl px-16 pb-24 pt-16 text-black text-22">
          <div class="place-self-center">
            <Icon name="bulb" class="w-28 h-28 place-self-center"></Icon>
        </div>
          <div class="text-center">{question.explanation}</div>
        </div>
      {/if}
    </div>
  </div>
  <!-- Images wrapper: side by side on mobile, display:contents on desktop so A/B join parent flex -->
  <div class="order-2 flex gap-8 sm:gap-16 xl:[display:contents]">
    <!-- Image A -->
    <div class="flex-1 xl:flex-none xl:order-1">
      <div class={`w-40 h-40 text-center place-content-center rounded-full text-white text-28 place-self-center relative z-20 ${imageClasses[0].circle}`}>
        {#if imageClasses[0].showIcon}
          <Icon name={imageClasses[0].circle.includes("false") ? "close" : "check"} class="w-28 h-28 place-self-center text-white fill-white" />
        {:else}
          A
        {/if}
      </div>
      <button
        class={`block w-full xl:w-277 aspect-square xl:aspect-auto xl:h-277 rounded-3xl border-8 overflow-hidden relative z-10 -mt-20 ${imageClasses[0].border} ${mode === "single" ? "cursor-pointer" : ""}`}
        on:click={() => selectAnswer(1)}
      >
        <Image class="w-full h-full object-contain" request={requests.assets} vars={{ file_name: question.image1?.file_name, preset: "medium.png" }} />
      </button>
      {#if mode == "class"}
        <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
          <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
          <input type="number" bind:value={pupilsA} disabled={true} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
           [&::-webkit-outer-spin-button]:appearance-none
           [&::-webkit-inner-spin-button]:appearance-none
           [&::-webkit-outer-spin-button]:m-0
           [&::-webkit-inner-spin-button]:m-0" />
          <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
        </div>
      {/if}
    </div>
    <!-- Image B -->
    <div class="flex-1 xl:flex-none xl:order-3">
      <div class={`w-40 h-40 text-center place-content-center rounded-full text-white text-28 place-self-center relative z-20 ${imageClasses[1].circle}`}>
        {#if imageClasses[1].showIcon}
          <Icon name={imageClasses[1].circle.includes("false") ? "close" : "check"} class="w-28 h-28 place-self-center text-white fill-white" />
        {:else}
          B
        {/if}
      </div>
      <button
        class={`block w-full xl:w-277 aspect-square xl:aspect-auto xl:h-277 rounded-3xl border-8 overflow-hidden relative z-10 -mt-20 ${imageClasses[1].border} ${mode === "single" ? "cursor-pointer" : ""}`}
        on:click={() => selectAnswer(2)}
      >
        <Image class="w-full h-full object-contain" request={requests.assets} vars={{ file_name: question.image2?.file_name, preset: "medium.png" }} />
      </button>
      {#if mode == "class"}
        <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
          <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
          <input type="number" disabled={true} bind:value={pupilsB} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
           [&::-webkit-outer-spin-button]:appearance-none
           [&::-webkit-inner-spin-button]:appearance-none
           [&::-webkit-outer-spin-button]:m-0
           [&::-webkit-inner-spin-button]:m-0" />
          <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
        </div>
      {/if}
    </div>
  </div>
</div>
<div class="px-16 md:px-24 mt-24">
  {#if mode == "class"}
    <button
      class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg"
      class:hidden={showSolution}
      on:click={saveData}>{$t("show solution")}</button
    >
  {/if}
  <button
    class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg flex gap-12"
    class:hidden={!showSolution}
    on:click={nextQuestion}
    >{$t("next")}
    <Icon name="arrow-right" class="w-28 h-28 place-self-center"></Icon>
  </button>
</div>
